import { ReactElement, useMemo } from 'react';
import classNames from 'classnames';
import { Heart, Plus } from 'lucide-react';
import useProductLikes from '../../../hooks/global/useProductLikes';
import RestrictedTooltip from '../../menu/restricted/RestrictedTooltip';
import { RESTRICTED_BG_CLASS, RESTRICTED_FG_HEX, RESTRICTED_IMAGE_STYLE } from '../../menu/restricted/constants';
import useTheme from '../../../hooks/ui/useTheme';
import { WithActualPrice } from '../../../providers/MenuProvider';
import { ProductInterface } from '../../../services/exports/Interfaces';
import PriceComparison from '../../menu/PriceComparison';
import useBasket from '../../../hooks/basket/useBasket';
import useConfig from '../../../hooks/ui/useConfig';
import { useTranslation } from 'react-i18next';
import useProductIdentifiers from '../../../hooks/menu/useProductIdentifiers';
import useProductPreviewPrice from '../../../features/offers/hooks/useProductPreviewPrice';

interface Props {
  product: WithActualPrice<ProductInterface>;
  index: number;
  toggleProduct: (product: ProductInterface) => void;
  addedQuantity?: number;
  isRestricted?: boolean;
  restrictedTooltip?: string;
}

const MostPopularProduct = (props: Props) => {
  const { product, toggleProduct, index, isRestricted } = props;
  const theme = useTheme();
  const { toggleProductLike, isLiked } = useProductLikes();
  const liked = isLiked(product.id);
  const { actualPrice, originalPrice } = useProductPreviewPrice(product);
  const { items } = useBasket();
  const { logo_url } = useConfig();

  const addedQuantity = useMemo(
    () => items.reduce((prev, curr) => (curr.product_id === product.id ? prev + curr.quantity : prev), 0),
    [items],
  );

  const { decoratedName } = useProductIdentifiers(product);

  const config = { ...props, addedQuantity };

  const restrictedImageStyle = isRestricted ? RESTRICTED_IMAGE_STYLE : undefined;

  return (
    <div
      className={classNames('flex flex-col w-full h-full', isRestricted ? 'cursor-default' : 'cursor-pointer')}
      onClick={() => !isRestricted && toggleProduct(product)}
    >
      <div className="relative aspect-square rounded-xl overflow-hidden bg-muted">
        <img
          src={product.thumbnail_url ?? logo_url}
          alt={product.name}
          className={classNames('w-full h-full object-cover', {
            '!object-contain bg-brand-card': !product.thumbnail_url,
          })}
          style={restrictedImageStyle}
        />
        <MostPopularProduct.BestSeller index={index} />
        <MostPopularProduct.AddButton {...config} />
      </div>
      <p
        className={classNames('text-sm font-semibold mt-xs text-brand-text-primary truncate', {
          'opacity-50': isRestricted,
        })}
      >
        {decoratedName}
      </p>
      <div className="flex items-center gap-xs text-sm text-muted-foreground">
        <span className={classNames({ 'opacity-50': isRestricted })}>
          <PriceComparison
            actualPrice={actualPrice}
            originalPrice={originalPrice}
            actualPriceStyle={{
              fontWeight: 600,
              color: theme.color.brand.text.primary,
              fontSize: 14,
            }}
            originalPriceStyle={{
              fontWeight: 600,
              fontSize: 14,
            }}
          />
        </span>
        <button
          type="button"
          className="flex items-center gap-2xs"
          onClick={(e) => {
            e.stopPropagation();
            toggleProductLike(product.id);
          }}
        >
          <Heart
            className={classNames('w-3.5 h-3.5', {
              'fill-danger text-danger': liked,
              'text-muted-foreground': !liked,
            })}
          />
          <span className="text-xs text-muted-foreground">{product.likes_count}</span>
        </button>
      </div>
    </div>
  );
};

export default MostPopularProduct;

MostPopularProduct.AddButton = ({ addedQuantity, isRestricted, restrictedTooltip }: Props): ReactElement => {
  const containerClassName = classNames(
    addButtonStyle,
    isRestricted ? `${RESTRICTED_BG_CLASS} cursor-not-allowed` : 'bg-brand-card',
  );
  const glyphColor = isRestricted ? RESTRICTED_FG_HEX : undefined;

  const button = (
    <button
      type="button"
      aria-disabled={isRestricted || undefined}
      className={containerClassName}
      onClick={isRestricted ? (e) => e.stopPropagation() : undefined}
    >
      {addedQuantity ? (
        <span
          className={classNames('text-sm font-semibold leading-none', !isRestricted && 'text-brand-text-primary')}
          style={glyphColor ? { color: glyphColor } : undefined}
        >
          {addedQuantity}
        </span>
      ) : (
        <Plus
          className={classNames('w-4 h-4', !isRestricted && 'text-brand-text-primary')}
          style={glyphColor ? { color: glyphColor } : undefined}
        />
      )}
    </button>
  );

  return <RestrictedTooltip title={isRestricted ? restrictedTooltip : undefined}>{button}</RestrictedTooltip>;
};

MostPopularProduct.BestSeller = ({ index }: { index: number }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:MostPopularProducts:MostPopularProduct' });
  const popularityRank = index + 1;

  if (popularityRank > 3) {
    return null;
  }

  return (
    <span className="absolute top-2 left-2 bg-brand-card/90 backdrop-blur-sm text-[11px] font-semibold px-xs py-[2px] rounded-full text-brand-text-primary shadow-sm">
      🏅 {t('most_popular', { rank: popularityRank })}
    </span>
  );
};

const addButtonStyle =
  'absolute bottom-2 right-2 rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:shadow-lg transition-shadow';
